import { useState, useEffect } from "react";
import DataLoaderOverlay from "components/DataLoaderOverlay";
import { useParams, Link } from "react-router-dom";
import { usePaymentAndUpload } from "hooks/usePaymentAndUpload";

type IProp = {
  isLoading: boolean;
}

const formatDate = (value: string | null | undefined) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const Field = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <div className="flex gap-2 mb-1">
      <span className="font-semibold min-w-[180px]">{label}:</span>
      <span>{value}</span>
    </div>
  ) : null;

const Order = ({ isLoading }: IProp) => {
  const { getOrderById } = usePaymentAndUpload();
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<any | null>(null);

  useEffect(() => {
    if (orderId) {
      getOrderById(orderId).then(setOrder);
    }
  }, [orderId]);

  return (
    <DataLoaderOverlay isLoading={isLoading}>
      <div className="pt-14 bg-main">
        <div className="flex flex-col justify-center">
          <div className="px-[40px] w-full">
            <div className="overflow-x-auto w-full sm:w-9/12 mx-auto">
              <h1 className="text-4xl mb-2">Order: {orderId}</h1>
              {order?.kit_id && (
                <p className="text-lg text-gray-500 mb-6">Kit ID: {order.kit_id}</p>
              )}

              <div className="bg-[#FCFCFC] p-8 my-8 space-y-8">

                {/* Client */}
                <Section title="Client">
                  <Field label="Name" value={order?.client} />
                  <Field label="Email" value={order?.email} />
                  <Field label="Phone" value={order?.phone} />
                </Section>

                {/* Kit Details */}
                <Section title="Kit Details">
                  <Field label="Program" value={order?.program} />
                  <Field label="Scope" value={order?.scope} />
                  <Field label="Fulfilled By" value={order?.fulfilled_by} />
                  <Field label="Kit Tracking #" value={order?.kit_tracking_number} />
                </Section>

                {/* Status & Dates */}
                <Section title="Status">
                  <Field label="Status" value={order?.status} />
                  <Field label="Order Date" value={formatDate(order?.order_date)} />
                  <Field label="Sample Received" value={formatDate(order?.sample_received_date)} />
                  <Field label="Date Created" value={formatDate(order?.date_created)} />
                </Section>

                {/* Notes */}
                {order?.notes && (
                  <Section title="Notes">
                    <p className="whitespace-pre-wrap text-gray-700">{order.notes}</p>
                  </Section>
                )}

                {/* Reports */}
                {order?.reports?.length > 0 && (
                  <Section title="Reports">
                    <div className="flex flex-wrap gap-3">
                      {order.reports.map((report_id: number) => (
                        <Link
                          key={report_id}
                          to={`/categories/${report_id}`}
                          className="underline text-blue-600 hover:text-blue-800"
                        >
                          Report {report_id}
                        </Link>
                      ))}
                    </div>
                  </Section>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </DataLoaderOverlay>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="flex flex-row pb-2">
    <div className="basis-1/6 pl-6">
      <h2 className="text-2xl">{title}</h2>
    </div>
    <div className="basis-5/6 font-helvetica pt-1">{children}</div>
  </div>
);

export default Order;
